import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StyledProps, ColorProps } from '../types/styled-utils';
import { latestTrackData } from '../utils/musicUtils';

const TerminalSection = styled.section`
  padding: 6rem 2rem;
  position: relative;
  /* Remove the solid background color */
  /* background-color: ${(props: StyledProps) => props.theme.colors.darkBg2}; */
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 4rem 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  
  span {
    background: linear-gradient(
      to right,
      ${(props: StyledProps) => props.theme.colors.gradientStart},
      ${(props: StyledProps) => props.theme.colors.gradientEnd}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
`;

const TerminalContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  background: rgba(18, 20, 37, 0.05);
  backdrop-filter: blur(3px);
  border: 1px solid rgba(255, 255, 255, 0.07);
`;

const TerminalHeader = styled.div`
  background: rgba(40, 40, 40, 0.05);
  padding: 0.8rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TerminalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
  justify-content: center;
  
  span {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const WindowControls = styled.div`
  display: flex;
  gap: 0.5rem;
  position: absolute;
  left: 0.8rem;
`;

interface ButtonProps {
  color: string;
}

const WindowButton: React.FC<ButtonProps> = ({ color }) => {
  return (
    <div
      style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: color
      }}
    />
  );
};

const TerminalBody = styled.div`
  height: 450px;
  background: transparent;
  padding: 1rem;
  overflow-y: auto;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  color: #E0E0E0;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    height: 350px;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 100, 100, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(120, 120, 120, 0.7);
  }
`;

const TerminalHistory = styled.div`
  margin-bottom: 1rem;
`;

const CommandLine = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
`;

const Prompt = styled.span`
  color: #87D441;
  margin-right: 0.5rem;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
`;

const Command = styled.span`
  color: #FFFFFF;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
`;

// First, let's create a utility function to convert text with URLs to clickable links
const convertLinksToHTML = (text: string) => {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Split the text by newlines to preserve formatting
  const lines = text.split('\n');
  
  // Process each line
  return lines.map((line, lineIndex) => {
    // Replace URLs with clickable links
    const lineWithLinks = line.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Return the line with a line break except for the last line
    return (
      <React.Fragment key={lineIndex}>
        <span dangerouslySetInnerHTML={{ __html: lineWithLinks }} />
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

// Now update the TerminalOutput component to make it handle HTML content
const TerminalOutput = styled.div`
  color: #F0F0F0;
  margin-bottom: 1rem;
  line-height: 1.4;
  white-space: pre-wrap;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
  
  a {
    color: #87D441; /* Green link color to match prompt */
    text-decoration: underline;
    transition: color 0.2s;
    
    &:hover {
      color: #a0ea5a;
      text-decoration: underline;
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const Input = styled.input`
  flex-grow: 1;
  background: transparent;
  border: none;
  color: #FFFFFF;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  outline: none;
  caret-color: #87D441;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 16px;
  background-color: #87D441;
  animation: blink 1s infinite;
  margin-left: 2px;
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

interface HistoryItem {
  command: string;
  output: string;
}

const Terminal: React.FC = () => {
  const [commandHistory, setCommandHistory] = useState<HistoryItem[]>([
    {
      command: 'help',
      output: 'Available commands:\n- music: Show music links\n- social: Display social media links\n- whoami: Display identity\n- ls -la projects: List projects\n- play latest_track: Play the latest track\n- clear: Clear terminal screen',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  
  const scrollToBottom = () => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [commandHistory]);
  
  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    const command = inputValue.trim().toLowerCase();
    let output = '';
    
    // Process commands
    if (command === 'help') {
      output = 'Available commands:\n- music: Show music links\n- social: Display social media links\n- whoami: Display identity\n- ls -la projects: List projects\n- play latest_track: Play the latest track\n- clear: Clear terminal screen';
    } else if (command === 'music') {
      output = 'Music Links:\n- Spotify: https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7\n- YouTube: https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA\n\nLatest releases on Spotify';
    } else if (command === 'social') {
      output = 'Connect with 6bit:\n- Spotify: https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7\n- YouTube: https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA\n- GitHub: https://github.com/modeofO';
    } else if (command === 'whoami') {
      output = 'Music producer and developer passionate about creating digital experiences.';
    } else if (command === 'ls -la projects') {
      output = 'total 4\ndrwxr-xr-x  2 6bit users 4096 Jan 1 2023 music/\ndrwxr-xr-x  2 6bit users 4096 Jan 1 2023 code/\ndrwxr-xr-x  2 6bit users 4096 Jan 1 2023 creative/\n-rw-r--r--  1 6bit users 8192 May 1 2023 ecliptic.md';
    } else if (command === 'project ecliptic' || command === 'cat projects/ecliptic.md') {
      output = 'Project: Ecliptic\n\nAI-Powered Beat-Synchronized Video Editor\n\nUpload your video clips and music to create a perfectly synchronized, AI-enhanced visual masterpiece that follows the beat of your audio.\n\nWebsite: https://ecliptic.gg\nGitHub: https://github.com/modeofO\n\nFeatures:\n- AI-powered video editing\n- Beat synchronization\n- Automated clip selection\n- Music-driven transitions';
    } else if (command === 'play latest_track') {
      // Stop audio if already playing
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        output = 'Stopped playing current track.';
      } else {
        // Play the latest track using Spotify embed
        setIsPlaying(true);
        
        // Use data from musicUtils
        const { title, trackId } = latestTrackData;
        output = `Now playing: ${title}\nSpotify: https://open.spotify.com/track/${trackId}\n\nUse 'play latest_track' again to stop playback.`;
        
        // Add Spotify embedded player to the page
        const spotifyEmbed = document.createElement('iframe');
        spotifyEmbed.src = `https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`;
        spotifyEmbed.width = '100%';
        spotifyEmbed.height = '80';
        spotifyEmbed.frameBorder = '0';
        spotifyEmbed.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
        
        // Remove existing player if any
        const existingPlayer = document.getElementById('spotify-terminal-player');
        if (existingPlayer) {
          existingPlayer.remove();
        }
        
        // Add new player
        const playerContainer = document.createElement('div');
        playerContainer.id = 'spotify-terminal-player';
        playerContainer.style.marginTop = '10px';
        playerContainer.appendChild(spotifyEmbed);
        
        // Add to terminal output
        setTimeout(() => {
          const lastOutput = document.querySelector('.terminal-output:last-child');
          if (lastOutput) {
            lastOutput.appendChild(playerContainer);
          }
        }, 100);
      }
    } else if (command === 'clear') {
      setCommandHistory([]);
      setInputValue('');
      return;
    } else {
      output = `Command not found: ${command}. Type 'help' to see available commands.`;
    }
    
    // Add to history
    setCommandHistory([...commandHistory, { command, output }]);
    setInputValue('');
  };
  
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  return (
    <TerminalSection id="terminal">
      <SectionHeading>
        Try the <span>Terminal</span>
      </SectionHeading>
      <SectionSubheading>
        Interact with this terminal to learn more about 6bit. Type 'help' to get started.
      </SectionSubheading>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <TerminalContainer onClick={focusInput}>
          <TerminalHeader>
            <WindowControls>
              <WindowButton color="#FF5F57" />
              <WindowButton color="#FEBC2E" />
              <WindowButton color="#28C840" />
            </WindowControls>
            <TerminalTitle>
              <span>6bit@terminal ~ — zsh</span>
            </TerminalTitle>
          </TerminalHeader>
          
          <TerminalBody ref={historyRef}>
            <TerminalHistory>
              {commandHistory.map((item, index) => (
                <div key={index}>
                  <CommandLine>
                    <Prompt>$</Prompt>
                    <Command>{item.command}</Command>
                  </CommandLine>
                  <TerminalOutput className="terminal-output">
                    {convertLinksToHTML(item.output)}
                  </TerminalOutput>
                </div>
              ))}
            </TerminalHistory>
            
            <form onSubmit={handleInputSubmit}>
              <InputContainer>
                <Prompt>$</Prompt>
                <Input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  autoFocus
                />
              </InputContainer>
            </form>
          </TerminalBody>
        </TerminalContainer>
      </motion.div>
      
      {/* Hidden audio element for web audio playback if needed */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </TerminalSection>
  );
};

export default Terminal; 